import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { OIDCProviders } from '#api';
import { AuthorizationError } from '#api/errors.ts';
import { oidcProviderQueryKeys } from '#api/query-keys.ts';
import type { Successful } from '#dn-types/api.ts';
import type { GetOIDCProviderResult } from './oidc-providers.ts';

export const adminOIDCProvidersQueryOptions = queryOptions({
  queryKey: oidcProviderQueryKeys.list(),
  queryFn: ({ signal }) => OIDCProviders.getOIDCProviders({ signal }),

  retry: (retries, error) => {
    if (error instanceof AuthorizationError) return false;
    return retries < 2;
  },
});

export function useSuspenseOIDCProvidersQuery() {
  return useSuspenseQuery(adminOIDCProvidersQueryOptions);
}

export function useSuspenseOIDCProviderQuery(providerId: string) {
  const queryClient = useQueryClient();

  return useSuspenseQuery({
    queryKey: oidcProviderQueryKeys.detail(providerId),
    queryFn: ({ signal }) => OIDCProviders.getOIDCProvider(providerId, { signal }),
    initialData: () => {
      const listQueryResults = queryClient.getQueriesData<Successful<GetOIDCProviderResult | null> | undefined>({
        queryKey: oidcProviderQueryKeys.list(),
      });
      const provider = listQueryResults
        .flatMap((hit) => hit[1]?.data)
        .find((d) => {
          return d?.id === providerId;
        });

      // If we found a match, use it as the initial data for the query
      if (provider) {
        return { data: provider };
      }
    },
    initialDataUpdatedAt: () =>
      queryClient.getQueryState<Successful<GetOIDCProviderResult | null> | undefined>(oidcProviderQueryKeys.list())
        ?.dataUpdatedAt,
  });
}

export function useAddAdminOIDCProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: OIDCProviders.addOIDCProvider,
    onSuccess: (data) => {
      queryClient.setQueryData(oidcProviderQueryKeys.list(), { data: [data] });
    },
  });
}

export function useEditAdminOIDCProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: OIDCProviders.editOIDCProvider,
    throwOnError: (error) => error instanceof AuthorizationError,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: oidcProviderQueryKeys.lists() });
    },
  });
}
